import { Box, IconButton, Typography } from '@mui/material';
import { IMemberDetails } from '@types';
import { CountryFlag, ProfilePicture } from 'components/common';
import EventIcon from '@mui/icons-material/EventOutlined';
import EmailIcon from '@mui/icons-material/EmailOutlined';
import { getCalendarHref } from '../utils/contact';
import { logEvent } from '@services';
import { getCountryCode } from '@utils';
import { useIsPhone } from '@hooks';
import { touchTargetSx } from 'theme/touchTarget';

type Props = {
  memberDetails: IMemberDetails;
};

const actionSx = {
  display: 'flex',
  flexDirection: { tablet: 'column' },
  alignItems: 'center',
  gap: 1,
} as const;

export const MemberInfo: React.FC<Props> = ({
  memberDetails: { member, project },
}) => {
  const isPhone = useIsPhone();

  return (
    <Box
      display="flex"
      flexDirection={{ xs: 'column', tablet: 'row' }}
      alignItems={{ tablet: 'center' }}
      justifyContent="space-between"
      gap={{ xs: 2, tablet: 0 }}
      pr={{ tablet: 2 }}
    >
      <Box
        display="flex"
        gap={2}
        sx={{
          textDecoration: 'none',
          color: 'inherit',
          alignItems: 'center',
        }}
      >
        <Box sx={{ position: 'relative' }}>
          <ProfilePicture
            url={member.avatarUrl}
            style={{
              width: 90,
              height: 90,
              borderRadius: 24,
            }}
          />
          {getCountryCode(member.legalLocation) && (
            <CountryFlag
              country={member.legalLocation}
              width={24}
              sx={{
                position: 'absolute',
                right: -4,
                bottom: -6,
              }}
            />
          )}
        </Box>
        <Box>
          <Typography variant="h2" sx={{ mb: 0.5 }}>
            {member.fullName}
          </Typography>
          <Typography color="text.secondary">
            {member.seniority} {member.stream}
          </Typography>
          <Typography color="text.secondary">{project.name}</Typography>
        </Box>
      </Box>
      <Box
        display="flex"
        justifyContent={{ tablet: 'flex-end' }}
        mx={{ xs: -2, tablet: 0 }}
        px={{ xs: 1, tablet: 0 }}
        bgcolor={{ xs: 'highlight.neutral', tablet: 'transparent' }}
      >
        <IconButton
          onClick={() => logEvent('email_team_member_click')}
          component="a"
          target="_blank"
          href={`mailto:${member.email}`}
          sx={{ color: 'text.link', borderRadius: 2, ...touchTargetSx }}
        >
          <Box sx={actionSx}>
            <EmailIcon />
            <Typography variant="caption">Write email</Typography>
          </Box>
        </IconButton>

        <IconButton
          onClick={() => logEvent('schedule_meeting_with_team_member_click')}
          component="a"
          href={getCalendarHref(member.fullName, member.email)}
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: 'text.link', borderRadius: 2, ...touchTargetSx }}
        >
          <Box sx={actionSx}>
            <EventIcon />
            <Typography variant="caption">Schedule 1-on-1</Typography>
          </Box>
        </IconButton>
      </Box>
      <>
        {project.deliveryManager && (
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              background: '#F6F8FC',
              display: 'flex',
              gap: 2,
              ...(isPhone && {
                p: 0,
                pb: 2,
                borderRadius: 0,
                background: 'none',
                borderBottom: 1,
                borderColor: 'divider',
              }),
            }}
          >
            <ProfilePicture url={project.deliveryManager.avatarUrl} />
            <Box>
              <Typography variant="body1">
                {project.deliveryManager.fullName}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Delivery Manager
              </Typography>
            </Box>
          </Box>
        )}
      </>
    </Box>
  );
};
